/**
 * HoursApprovalControl - Approve / revoke the recorded hours on an
 * hour-tracked Work Order.
 *
 * Only rendered for work orders that track hours ("Require Hours" ticked,
 * or a T&M sub-type that forces it on). Those hours are what the customer
 * is ultimately billed for, so they don't go straight from the field to an
 * invoice — they're submitted when the work is declared finished, and have
 * to be signed off before the work order can be invoiced.
 *
 * Three states, in the order a work order passes through them:
 *
 *   1. Not yet submitted — nothing to approve. Shows what is still
 *      outstanding rather than an action the user can't complete.
 *   2. Submitted, awaiting approval — offers the two approver paths:
 *        "Approve as Admin"   : admins + super admins. Single click.
 *        "Customer Approval"  : opens a picker for the work order's
 *                               primary contact, in the same
 *                               hand-the-device-over model as the
 *                               existing signature capture.
 *   3. Approved — shows who signed off + when, plus a Revoke button so a
 *      corrected hour figure can be sent back around.
 *
 * Deliberately shaped like EstimateApprovalControl. The two approve
 * different things at opposite ends of the job, though: that one approves
 * the COST before the work, this one approves the HOURS after it.
 *
 * A pure UI shell over hoursApprovalService — the parent owns the data,
 * passes the current state, and refetches via `onChange()`.
 */
import React, { useState } from 'react';
import { Button } from '../../atoms';
import { Modal } from '../Modal';
import { useAuth } from '../../../hooks/useAuth';
import {
    hoursApprovalService,
    HoursApproverType,
} from '../../../services/hoursApprovalService';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

/**
 * Subset of the Contact shape the picker needs. The parent maps this
 * from the company contact list it already loads for the detail page.
 */
export interface HoursApprovalContactOption {
    id: number;
    name: string;
}

/**
 * Snapshot of the work order's hours sign-off state. `submittedAt` being
 * null is what separates "still being worked" from "awaiting approval" —
 * approving hours the field hasn't handed off yet would sign off a moving
 * target, and the server rejects it.
 */
export interface HoursApprovalState {
    submittedAt: string | null;
    submittedByName: string | null;
    approvedAt: string | null;
    approvedByUserName: string | null;
    approvedByContactName: string | null;
}

/**
 * Props for HoursApprovalControl.
 *
 * @property recordId    The id of the work order whose hours are approved
 * @property approval    Current submit + approval state
 * @property contacts    Contacts offered for "customer approval". The
 *                       parent narrows this to the work order's primary
 *                       contact when it has one — the server enforces the
 *                       same rule.
 * @property canApprove  Whether the current user can act at all (parent
 *                       computes: admin/manager tier)
 * @property onChange    Called after a successful approve/revoke so the
 *                       parent can refetch
 * @property className   Optional extra classes for the outer wrapper
 */
export interface HoursApprovalControlProps {
    recordId: string;
    approval: HoursApprovalState;
    contacts: HoursApprovalContactOption[];
    canApprove: boolean;
    onChange: () => void;
    className?: string;
}

/* ============================================
 * COMPONENT
 * ============================================ */

export const HoursApprovalControl: React.FC<HoursApprovalControlProps> = ({
    recordId,
    approval,
    contacts,
    canApprove,
    onChange,
    className,
}) => {
    /** Gates the "Approve as Admin" button — the server enforces the same
     *  set, so this only decides whether the affordance is offered. */
    const { hasAnyRole } = useAuth();
    const isAdminTier = hasAnyRole(['super_admin', 'admin']);

    /* Local UI state — modal visibility, selected contact, pending flags. */
    const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
    const [selectedContactId, setSelectedContactId] = useState<string>('');
    const [isPending, setIsPending] = useState(false);
    const [error, setError] = useState<string | null>(null);

    /** Posts the approval, then notifies the parent on success. */
    const callApprove = async (approverType: HoursApproverType, contactId?: number) => {
        setIsPending(true);
        setError(null);
        try {
            await hoursApprovalService.approveWorkOrder(recordId, { approverType, contactId });
            onChange();
            setIsCustomerModalOpen(false);
            setSelectedContactId('');
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to approve hours');
        } finally {
            setIsPending(false);
        }
    };

    /** Same shape but for revocation. */
    const callRevoke = async () => {
        if (!window.confirm('Revoke hours approval? The work order will go back to awaiting approval and cannot be billed until it is approved again.')) return;
        setIsPending(true);
        setError(null);
        try {
            await hoursApprovalService.revokeWorkOrder(recordId);
            onChange();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to revoke');
        } finally {
            setIsPending(false);
        }
    };

    const isApproved = approval.approvedAt !== null;
    const isSubmitted = approval.submittedAt !== null;
    const approverDisplayName = approval.approvedByUserName || approval.approvedByContactName;

    /* ---------------------------------------------------------------
     * Render — approved branch
     * --------------------------------------------------------------- */
    if (isApproved) {
        return (
            <div className={['hours-approval-control', 'hours-approval-control--approved', 'flex flex-wrap items-center gap-12px', className ?? ''].join(' ')} data-ref="organism-hours-approval-control">
                <div className="hours-approval-control__summary text-13px text-brand-text">
                    <span className="font-medium text-green-700">Hours approved</span>
                    {approverDisplayName && (
                        <> by <span className="font-medium">{approverDisplayName}</span></>
                    )}
                    {approval.approvedAt && (
                        <> on <span>{new Date(approval.approvedAt).toLocaleDateString()}</span></>
                    )}
                    {/* The whole point of the gate — say plainly that the
                        work order has cleared it. */}
                    <span className="block text-12px text-brand-text-muted">Ready to bill.</span>
                </div>
                {canApprove && (
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={callRevoke}
                        isLoading={isPending}
                    >
                        Revoke
                    </Button>
                )}
                {error && <span className="text-12px text-red-700">{error}</span>}
            </div>
        );
    }

    /* ---------------------------------------------------------------
     * Render — not submitted yet. There is nothing to approve until the
     * work is declared finished, so no action is offered to anyone.
     * --------------------------------------------------------------- */
    if (!isSubmitted) {
        return (
            <div className={['hours-approval-control', 'text-13px text-brand-text-muted', className ?? ''].join(' ')} data-ref="organism-hours-approval-control">
                Awaiting submission — hours are approved once the work order is submitted for review or completed.
            </div>
        );
    }

    /* ---------------------------------------------------------------
     * Render — submitted, awaiting approval
     * --------------------------------------------------------------- */
    if (!canApprove) {
        return (
            <div className={['hours-approval-control', 'text-13px text-brand-text-muted', className ?? ''].join(' ')} data-ref="organism-hours-approval-control">
                Awaiting approval
                {approval.submittedByName && <> — submitted by {approval.submittedByName}</>}
            </div>
        );
    }

    return (
        <div className={['hours-approval-control', className ?? ''].join(' ')} data-ref="organism-hours-approval-control">
            <p className="hours-approval-control__status text-12px text-brand-text-muted mb-8px">
                Submitted
                {approval.submittedByName && <> by {approval.submittedByName}</>}
                {approval.submittedAt && <> on {new Date(approval.submittedAt).toLocaleDateString()}</>}
                {' '}— approve to make this work order billable.
            </p>
            <div className="hours-approval-control__actions flex flex-wrap items-center gap-8px">
                {isAdminTier && (
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={() => callApprove('admin')}
                        isLoading={isPending}
                    >
                        Approve Hours
                    </Button>
                )}
                <Button
                    variant={isAdminTier ? 'secondary' : 'primary'}
                    size="sm"
                    onClick={() => setIsCustomerModalOpen(true)}
                    disabled={isPending}
                >
                    Customer Approval
                </Button>
                {error && <span className="text-12px text-red-700">{error}</span>}
            </div>

            {/* Customer approval modal — pick the contact who is approving */}
            <Modal
                isOpen={isCustomerModalOpen}
                onClose={() => setIsCustomerModalOpen(false)}
                title="Customer hours approval"
                size="sm"
            >
                <div className="hours-approval-control__form space-y-12px">
                    <p className="text-13px text-brand-text-muted">
                        Select the customer contact who is approving the hours recorded on this
                        work order. Once approved, it can be billed.
                    </p>
                    {contacts.length === 0 ? (
                        <div className="hours-approval-control__note text-13px text-brand-text-muted px-12px py-8px bg-brand-surface border border-brand-border rounded-md">
                            No contact on file for this work order. Add one in the Contacts
                            section, or set the work order&apos;s contact, then return here.
                        </div>
                    ) : (
                        <select
                            value={selectedContactId}
                            onChange={(e) => setSelectedContactId(e.target.value)}
                            className="block w-full rounded-md border border-brand-border px-12px py-10px text-16px desktop:text-14px font-sans focus:outline-none focus:ring-2 focus:ring-brand-primary bg-white"
                        >
                            <option value="">Choose a contact…</option>
                            {contacts.map((c) => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                        </select>
                    )}

                    <div className="hours-approval-control__actions flex justify-end gap-8px pt-8px">
                        <Button
                            variant="ghost"
                            onClick={() => setIsCustomerModalOpen(false)}
                            disabled={isPending}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={() => selectedContactId && callApprove('contact', Number(selectedContactId))}
                            disabled={!selectedContactId || contacts.length === 0}
                            isLoading={isPending}
                        >
                            Record Approval
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default HoursApprovalControl;
